import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import { issueStreamTicket, PositionFramePayload } from '@core/api/sse';
import { qk } from '@core/api/queryKeys';
import { getEnv } from '@core/config/env';
import type { LatestPositionsResponse, PositionFix } from '@core/types/api';

import { reconnectDelayMs } from './backoff';
import { mergePositions } from './mergePositions';

export type LiveStatus = 'connecting' | 'live' | 'reconnecting' | 'polling' | 'paused' | 'offline';

interface LiveTrackingValue {
  status: LiveStatus;
  /** Epoch ms of the next reconnect attempt, for a countdown. */
  nextRetryAt: number | null;
  reconnect: () => void;
}

const LiveTrackingContext = createContext<LiveTrackingValue | null>(null);

/** Hidden this long before the stream is dropped. Short enough to save a backgrounded laptop's battery. */
const HIDDEN_GRACE_MS = 60_000;
/** After this many consecutive failures, stop trying to stream and poll instead. */
const FAILURES_BEFORE_POLLING = 5;
/** The server heartbeats every 15s; 90s of silence means the connection is dead. */
const HEARTBEAT_TIMEOUT_MS = 90_000;
/** Micro-batch window before touching the query cache. */
const FLUSH_MS = 120;

export function LiveTrackingProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const [nextRetryAt, setNextRetryAt] = useState<number | null>(null);

  const sourceRef = useRef<EventSource | null>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const attemptRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);
  /** Set synchronously before the ticket await, so a second caller cannot slip past. */
  const connectingRef = useRef(false);
  const lastBeatRef = useRef(0);
  const bufferRef = useRef<PositionFix[]>([]);
  const flushTimerRef = useRef<number | null>(null);

  /**
   * Applies a batch to the cache in one write.
   *
   * A reconnect replay can deliver forty fixes at once. Without batching that is forty
   * `setQueryData` calls and forty renders of the map in a single tick.
   */
  const flush = useCallback(() => {
    flushTimerRef.current = null;
    const batch = bufferRef.current;
    if (batch.length === 0) return;
    bufferRef.current = [];

    queryClient.setQueryData<LatestPositionsResponse>(qk.positions.latest(), (previous) =>
      mergePositions(previous, batch),
    );
  }, [queryClient]);

  const enqueue = useCallback(
    (fix: PositionFix) => {
      bufferRef.current.push(fix);
      flushTimerRef.current ??= window.setTimeout(flush, FLUSH_MS);
    },
    [flush],
  );

  const disconnect = useCallback(() => {
    connectingRef.current = false;
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    sourceRef.current?.close();
    sourceRef.current = null;
  }, []);

  const scheduleReconnect = useCallback((connect: () => void) => {
    if (stoppedRef.current || retryTimerRef.current !== null) return;

    const attempt = ++attemptRef.current;
    if (attempt > FAILURES_BEFORE_POLLING) {
      // Give up on streaming and let the polling query take over. The map keeps
      // updating, just less promptly -- far better than an indefinite retry loop.
      setStatus('polling');
      setNextRetryAt(null);
      return;
    }

    const delay = reconnectDelayMs(attempt);
    setStatus('reconnecting');
    setNextRetryAt(Date.now() + delay);
    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      connect();
    }, delay);
  }, []);

  const connect = useCallback(async () => {
    // StrictMode mounts effects twice in development, and a resumed tab can race with a
    // pending retry. Without a guard the app holds two streams and every fix arrives
    // twice, which looks like a server bug.
    //
    // `sourceRef` alone is NOT enough: this function awaits the ticket request, and
    // during that await no EventSource exists yet, so a second caller sails straight
    // through. The synchronous flag is what actually closes the window.
    if (stoppedRef.current || sourceRef.current || connectingRef.current) return;
    connectingRef.current = true;

    setStatus(attemptRef.current === 0 ? 'connecting' : 'reconnecting');

    let url: string;
    try {
      const { ticket } = await issueStreamTicket();
      const env = getEnv();
      const target = new URL(env.SSE_PATH, env.GATEWAY || window.location.origin);
      target.searchParams.set('ticket', ticket);
      if (lastEventIdRef.current) {
        target.searchParams.set('lastEventId', lastEventIdRef.current);
      }
      url = target.toString();
    } catch {
      // The ticket call itself failed -- offline, or the session is gone. Back off
      // rather than hammering the endpoint.
      connectingRef.current = false;
      scheduleReconnect(() => void connect());
      return;
    }

    connectingRef.current = false;

    // The component may have unmounted, or another attempt may have won, while the
    // ticket request was in flight.
    if (stoppedRef.current || sourceRef.current) return;

    const source = new EventSource(url);
    sourceRef.current = source;

    const beat = () => {
      lastBeatRef.current = Date.now();
    };

    source.addEventListener('open', () => {
      attemptRef.current = 0;
      setNextRetryAt(null);
      setStatus('live');
      beat();
      // The replay window may not cover the whole gap, so always resync against an
      // authoritative snapshot rather than trusting replay alone.
      void queryClient.invalidateQueries({ queryKey: qk.positions.latest() });
    });

    source.addEventListener('position', (event) => {
      const message = event as MessageEvent<string>;
      if (message.lastEventId) lastEventIdRef.current = message.lastEventId;
      beat();

      let raw: unknown;
      try {
        raw = JSON.parse(message.data);
      } catch {
        return; // One unparseable frame must not kill the stream.
      }

      const parsed = PositionFramePayload.safeParse(raw);
      if (!parsed.success) return;

      const fixes = Array.isArray(parsed.data) ? parsed.data : [parsed.data];
      fixes.forEach((fix) => enqueue(fix as PositionFix));
    });

    source.addEventListener('alert', (event) => {
      const message = event as MessageEvent<string>;
      if (message.lastEventId) lastEventIdRef.current = message.lastEventId;
      beat();
      void queryClient.invalidateQueries({ queryKey: qk.alerts.all() });
    });

    source.addEventListener('tracker', (event) => {
      const message = event as MessageEvent<string>;
      if (message.lastEventId) lastEventIdRef.current = message.lastEventId;
      beat();
      void queryClient.invalidateQueries({ queryKey: qk.trackers.all() });
    });

    source.addEventListener('heartbeat', beat);

    // Sent instead of a replay when the client missed too much to replay usefully: the
    // authoritative snapshot is cheaper and complete.
    source.addEventListener('resync', () => {
      beat();
      void queryClient.invalidateQueries({ queryKey: qk.positions.latest() });
    });

    source.addEventListener('error', () => {
      // THE most important line in this file.
      //
      // EventSource auto-reconnects to the SAME url -- which carries our single-use,
      // 30-second ticket. Native retry therefore replays a spent ticket, gets 401,
      // retries again, and never recovers: a tight loop that DDoSes our own gateway.
      // Closing here hands control to our own backoff.
      source.close();
      if (sourceRef.current === source) sourceRef.current = null;
      scheduleReconnect(() => void connect());
    });
  }, [enqueue, queryClient, scheduleReconnect]);

  useEffect(() => {
    // Reset at effect start, not only in cleanup: StrictMode remounts this effect, and a
    // stoppedRef left true from the first teardown would prevent the second from ever
    // connecting.
    stoppedRef.current = false;
    void connect();

    return () => {
      stoppedRef.current = true;
      disconnect();
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, [connect, disconnect]);

  /**
   * Silent-stream watchdog.
   *
   * A load balancer can drop an idle connection without the browser ever firing `error`,
   * leaving the client convinced it is live while receiving nothing. On a 10-minute duty
   * cycle a quiet stream is completely normal, so silence alone proves nothing -- the
   * server's 15-second heartbeat is what makes the distinction possible at all.
   */
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (status !== 'live') return;
      if (Date.now() - lastBeatRef.current > HEARTBEAT_TIMEOUT_MS) {
        disconnect();
        attemptRef.current = 0;
        void connect();
      }
    }, 15_000);
    return () => clearInterval(timer);
  }, [status, connect, disconnect]);

  /** Drop the stream while the tab is hidden; `refetchOnWindowFocus` covers the gap. */
  useEffect(() => {
    let hideTimer: number | null = null;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hideTimer = window.setTimeout(() => {
          disconnect();
          setStatus('paused');
        }, HIDDEN_GRACE_MS);
        return;
      }

      if (hideTimer !== null) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      if (!sourceRef.current) {
        attemptRef.current = 0;
        void connect();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (hideTimer !== null) clearTimeout(hideTimer);
    };
  }, [connect, disconnect]);

  const value = useMemo<LiveTrackingValue>(
    () => ({
      status,
      nextRetryAt,
      reconnect: () => {
        disconnect();
        attemptRef.current = 0;
        void connect();
      },
    }),
    [status, nextRetryAt, connect, disconnect],
  );

  return <LiveTrackingContext value={value}>{children}</LiveTrackingContext>;
}

export function useLiveTracking(): LiveTrackingValue {
  const value = use(LiveTrackingContext);
  if (!value) {
    throw new Error('useLiveTracking must be used within <LiveTrackingProvider>');
  }
  return value;
}
